import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { applyUserLanguage } from "@/i18n";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null while loading, false when logged out
  const [ready, setReady] = useState(false);
  // React-Router's `navigate` fn, injected by <NavigatorBinder /> in App.js.
  // Kept in a ref so logout() can SPA-navigate without a full page reload
  // (which used to flash the marketing landing = "old design" before /login).
  const navRef = useRef(null);
  const _bindNavigate = (nav) => { navRef.current = nav; };

  useEffect(() => {
    if (!localStorage.getItem("garage_session")) {
      setUser(false);
      setReady(true);
      return;
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); applyUserLanguage(r.data); })
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("garage_session", "1");
    localStorage.setItem("garage_user", JSON.stringify(data.user));
    setUser(data.user);
    applyUserLanguage(data.user);
    return data.user;
  };

  // Expose setUser so pages like MyProfile can push the updated user object
  // back into context after editing their name / email.
  const updateUser = (u) => {
    if (!u) return;
    localStorage.setItem("garage_user", JSON.stringify(u));
    setUser(u);
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("garage_session", "1");
    localStorage.setItem("garage_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("garage_session");
    localStorage.removeItem("garage_user");
    setUser(false);
    // SPA-nav — no page reload, no landing flash.  Fall back to
    // window.location only if the binder hasn't registered yet (should
    // never happen in practice since <NavigatorBinder /> mounts once).
    if (navRef.current) navRef.current("/login", { replace: true });
    else window.location.href = "/login";
  };

  /**
   * Central permission check.  Owner bypasses everything, staff need the exact
   * `perm` string in their permissions list.  Used to hide UI (nav items,
   * buttons); the server always re-checks on sensitive endpoints.
   */
  const hasPermission = (perm) => {
    if (!user) return false;
    // Super-admins can only "see" tenant sections while they are actively
    // impersonating a garage.  Outside impersonation their world is limited
    // to the Super Admin dashboard so tenant data can never leak.
    if (user.role === "super_admin") return !!user.impersonating;
    if (user.role === "owner") return true;
    const perms = user.permissions || [];
    if (perm === "suppliers.view" && ["inventory.edit", "delivery_scan.use", "suppliers.edit"].some((p) => perms.includes(p))) return true;
    return perms.includes(perm);
  };

  /**
   * Map each nav route -> the minimal permission that unlocks it. Kept in sync
   * with backend PERMISSION_CATALOG and DashboardLayout NAV. Order defines the
   * fallback landing page when a staff member has NO permission for /dashboard.
   */
  // Startpagina per rol: monteur → werkbonnen, balie → agenda, magazijn → inkoop/verwachte leveringen, boekhouding → facturen. Voorraad als laatste.
  const ROUTE_PERM = [
    ["/dashboard",      "reports.view"],
    ["/repairs",        "repairs.view"],
    ["/calendar",       "calendar.view"],
    ["/workboard",      "calendar.view"],
    ["/bay-board",      "calendar.view"],
    ["/purchase-orders", "purchase_orders.view"],
    ["/invoices",       "invoices.view"],
    ["/customers",      "customers.view"],
    ["/vehicles",       "customers.view"],
    ["/cash-register",  "cash.view"],
    ["/suppliers",      "suppliers.view"],
    ["/inventory",      "inventory.view"],
    ["/accounts",       "accounts.view"],
    ["/bank",           "bank.view"],
    ["/reminders",      "reminders.view"],
    ["/delivery-scan",  "delivery_scan.use"],
    ["/reports",        "reports.view"],
  ];

  /** Compute the first allowed path for an arbitrary user (used right after
   *  login when React state hasn't propagated yet). */
  const pathForUser = (u) => {
    if (!u) return "/login";
    // Super-admins who are impersonating a tenant get dropped into the
    // garage's own dashboard so the workflow feels natural.  Otherwise
    // they're locked to the platform admin console.
    if (u.role === "super_admin") return u.impersonating ? "/dashboard" : "/super-admin";
    if (u.role === "owner") return "/dashboard";
    const perms = new Set(u.permissions || []);
    for (const [path, perm] of ROUTE_PERM) {
      if (perms.has(perm)) return path;
    }
    return "/my-profile";
  };

  /** First route the current user is allowed to see (or /my-profile fallback). */
  const firstAllowedPath = () => pathForUser(user);

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, hasPermission, firstAllowedPath, pathForUser, setUser: updateUser, _bindNavigate }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
